// packages/ui/src/components/DashboardWS.ts

export type DashboardEventHandler = (event: { type: string; payload?: unknown; seq?: number }) => void

export class DashboardWS {
  private ws: WebSocket | null = null
  private attempt = 0
  private lastSeq = 0
  private destroyed = false
  private handlers = new Set<DashboardEventHandler>()

  constructor(private readonly host: string = location.host) {}

  onEvent(handler: DashboardEventHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  async connect(): Promise<void> {
    if (this.destroyed) return
    if (this.ws) return  // already connected or connecting

    const token = localStorage.getItem('droid_token')
    if (!token) return  // not signed in — wait for AuthProvider to set token before connecting

    try {
      const resp = await fetch('/api/dashboard/ws-ticket', {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) {
        this.scheduleReconnect()
        return
      }
    } catch {
      this.scheduleReconnect()
      return
    }

    const ws = new WebSocket(`wss://${this.host}/api/dashboard/ws`)
    this.ws = ws

    ws.onopen = () => {
      this.attempt = 0
      ws.send(JSON.stringify({ type: 'hello', lastSeq: this.lastSeq }))
    }

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data as string)
        if (typeof msg.seq === 'number') this.lastSeq = msg.seq
        for (const handler of this.handlers) {
          handler(msg)
        }
      } catch { /* ignore malformed */ }
    }

    ws.onclose = () => {
      this.ws = null
      if (!this.destroyed) this.scheduleReconnect()
    }

    ws.onerror = () => ws.close()
  }

  private scheduleReconnect(): void {
    const jitter = 0.5 + Math.random() * 0.5  // 0.5-1.0
    this.attempt++
    const delay = Math.min(30_000, 1_000 * Math.pow(2, this.attempt)) * jitter
    setTimeout(() => this.connect(), delay)
  }

  destroy(): void {
    this.destroyed = true
    this.ws?.close()
    this.ws = null
    this.handlers.clear()
  }
}
